import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Database } from '@/integrations/supabase/types';

type InvoiceTemplate = Database['public']['Tables']['invoice_templates']['Row'];
type InvoiceTemplateInsert = Database['public']['Tables']['invoice_templates']['Insert'];
type InvoiceTemplateUpdate = Database['public']['Tables']['invoice_templates']['Update'];
export type DocumentTemplatePreset = Database['public']['Tables']['document_template_presets']['Row'];
export type DocumentPresetType = 'preliminary' | 'voucher';

type VersionedInvoiceTemplate = InvoiceTemplate & { version?: number };

export const useInvoiceTemplate = (hotelId: string | undefined) => {
  const [template, setTemplate] = useState<VersionedInvoiceTemplate | null>(null);
  const [hotelName, setHotelName] = useState<string>('');
  const [presets, setPresets] = useState<DocumentTemplatePreset[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchTemplate = async () => {
    if (!hotelId) {
      setLoading(false);
      return;
    }

    try {
      const [templateRes, hotelRes, presetsRes] = await Promise.all([
        supabase
          .from('invoice_templates')
          .select('*')
          .eq('hotel_id', hotelId)
          .eq('is_default', true)
          .maybeSingle(),
        supabase
          .from('hotels')
          .select('name')
          .eq('id', hotelId)
          .maybeSingle(),
        supabase
          .from('document_template_presets')
          .select('*')
          .eq('is_active', true)
          .order('sort_order'),
      ]);

      if (templateRes.error && templateRes.error.code !== 'PGRST116') {
        throw templateRes.error;
      }
      if (presetsRes.error) throw presetsRes.error;

      setTemplate(templateRes.data);
      setPresets(presetsRes.data || []);
      // Приоритет: hotel_display_name из шаблона → hotels.name → fallback
      const displayName = (templateRes.data as any)?.hotel_display_name 
        || hotelRes.data?.name 
        || 'Отель';
      setHotelName(displayName);
    } catch (error: any) {
      console.error('Error fetching invoice template:', error);
      toast({
        title: 'Ошибка загрузки шаблона',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Сброс состояния при смене отеля
    setTemplate(null);
    setHotelName('');

    fetchTemplate();

    // Real-time подписка на изменения
    const channel = supabase
      .channel(`invoice-template-${hotelId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'invoice_templates',
          filter: `hotel_id=eq.${hotelId}`,
        },
        (payload) => {
          console.log('Invoice template changed:', payload);
          if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') {
            setTemplate(payload.new as VersionedInvoiceTemplate);
          } else if (payload.eventType === 'DELETE') {
            setTemplate(null);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [hotelId]);

  const updateTemplate = async (updates: InvoiceTemplateUpdate) => {
    if (!hotelId) return false;

    try {
      const { error } = await supabase.rpc('save_invoice_template_atomic', {
        p_hotel_id: hotelId,
        p_template_id: template?.id || null,
        p_expected_version: template?.version || 0,
        p_settings: updates as unknown as Database['public']['Functions']['save_invoice_template_atomic']['Args']['p_settings'],
      });

      if (error) throw error;

      toast({ title: 'Шаблоны сохранены', description: 'Изменения применены целиком' });

      await fetchTemplate();
      return true;
    } catch (error: any) {
      console.error('Error updating invoice template:', error);
      toast({
        title: 'Ошибка сохранения',
        description: error.code === '40001' ? 'Шаблон уже изменил другой администратор. Данные обновлены — повторите правку.' : error.message,
        variant: 'destructive',
      });
      if (error.code === '40001') await fetchTemplate();
      return false;
    }
  };

  const applyPreset = async (presetKey: string, documentType: DocumentPresetType) => {
    if (!hotelId) return false;

    try {
      const { error } = await supabase.rpc('apply_document_preset_atomic', {
        p_hotel_id: hotelId,
        p_template_id: template?.id || null,
        p_expected_version: template?.version || 0,
        p_preset_key: presetKey,
        p_document_type: documentType,
      });

      if (error) throw error;
      await fetchTemplate();
      toast({
        title: 'Пресет применён',
        description: documentType === 'preliminary'
          ? 'Изменён только предварительный расчёт'
          : 'Изменён только ваучер',
      });
      return true;
    } catch (error: any) {
      console.error('Error applying document preset:', error);
      toast({
        title: 'Не удалось применить пресет',
        description: error.code === '40001'
          ? 'Шаблон уже изменил другой администратор. Данные обновлены — повторите действие.'
          : error.message,
        variant: 'destructive',
      });
      if (error.code === '40001') await fetchTemplate();
      return false;
    }
  };

  return {
    template,
    presets,
    hotelName,
    loading,
    updateTemplate,
    applyPreset,
    refetch: fetchTemplate,
  };
};
